import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './ImageUploader.css';
import { Canvas, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls, Center, Environment } from '@react-three/drei';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import * as THREE from 'three';

const ImageUploader = ({ mode, setMode, onImageGenerated, environmentImage, styleImage, onGenerationStart, onGenerationEnd, selectedTexture, showTextureDots, userData }) => {

  // const [mode, setMode] = useState('upload'); // MOVED TO APP.JS
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [textureDots, setTextureDots] = useState([]);
  const pollingIntervalRef = useRef(null);


  // NOUV: State pour le carrousel (0=Original, 1=Généré)
  const [viewIndex, setViewIndex] = useState(0);


  // AUTO-SWITCH vers l'onglet IMAGE et la vue GÉNÉRÉE quand un résultat arrive
  // Sauf si on est en train de dessiner (pour ne pas perdre le croquis)
  useEffect(() => {
    if (result && result.generated_image_url) {
      setMode('upload');
      setViewIndex(history.length > 0 ? history.length - 1 : 1); // Set to latest generated image
    }

  }, [result, setMode]); // Removed 'mode' to prevent lock-in after generation

  // Helper function for processing textures (defined inside component to access state/props if needed, 
  // but better as pure function if possible. Let's define it here for simplicity)
  const processTextures = async (imageSource, dots, formData, isPercentage = true) => {
    let texturePrompt = "";

    // 1. Create temporary canvas and draw image
    const tempCanvas = document.createElement('canvas');
    const ctx = tempCanvas.getContext('2d');
    const img = new Image();
    img.src = imageSource;
    await new Promise(resolve => { img.onload = resolve; });

    tempCanvas.width = img.width;
    tempCanvas.height = img.height;
    ctx.drawImage(img, 0, 0);

    // 2. Draw dots
    if (dots.length > 0) {
      dots.forEach(dot => {
        let dotX, dotY;
        if (isPercentage) {
          dotX = (dot.x / 100) * tempCanvas.width;
          dotY = (dot.y / 100) * tempCanvas.height;
        } else {
          // Normalized 0-1
          dotX = dot.x * tempCanvas.width;
          dotY = dot.y * tempCanvas.height;
        }

        const radius = Math.max(tempCanvas.width, tempCanvas.height) * 0.01; // Increased from 0.005 to 0.01 (1%) for better AI visibility

        ctx.beginPath();
        ctx.arc(dotX, dotY, radius, 0, 2 * Math.PI);
        ctx.fillStyle = dot.texture.color;
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.lineWidth = radius * 0.3; // Slightly thicker border
        ctx.stroke();
      });

      // 3. Generate blob
      const blob = await new Promise(resolve => tempCanvas.toBlob(resolve, 'image/png'));
      formData.append('image', blob, "processed_image.png");

      // 4. Build prompt and append texture files
      const usedTextures = [...new Set(dots.map(d => d.texture))];
      usedTextures.forEach((texture, index) => {
        if (texture.file) {
          formData.append(`texture_${index}`, texture.file);
          texturePrompt += ` \n- ZONE PASTILLE ${texture.color.toUpperCase()} : Remplacer par le MATÉRIAU EXACT de l'image de texture jointe n°${index + 1}. Adapte l'échelle de la texture au bâtiment.`;
        } else {
          texturePrompt += ` \n- ZONE PASTILLE ${texture.color.toUpperCase()} : Utilise précisément du ${texture.name}.`;
        }
      });
      texturePrompt += "\n\nCRITIQUE : Les pastilles colorées sont des marqueurs de position GÉOMÉTRIQUEMENT EXACTS. Ne les ignore pas. Supprime les pastilles dans le rendu final.";

    } else {
      // No dots, just append the original image (still via canvas to consistency)


      const blob = await new Promise(resolve => tempCanvas.toBlob(resolve, 'image/png'));
      formData.append('image', blob, "source_image.png");
    }

    return texturePrompt;
  };

  // --- ÉTATS MODEL 3D ---
  const [modelFile, setModelFile] = useState(null);
  const [modelUrl, setModelUrl] = useState(null);
  const [modelDots, setModelDots] = useState([]); // { position: [x,y,z], texture: ... }
  const canvas3dRef = useRef(null);
  const [glContext, setGlContext] = useState(null);

  // History State
  const [history, setHistory] = useState([]); // Array of { url: string, type: 'original'|'generated' }

  // Moved here to access 'history'
  const prevHistoryLength = useRef(0);
  useEffect(() => {
    if (history.length > prevHistoryLength.current) {
      setViewIndex(history.length - 1);
    }
    prevHistoryLength.current = history.length;
  }, [history]);

  const [lastPrompt, setLastPrompt] = useState('');

  // --- REFERENCE POUR L'INPUT FICHIER (LA TÉLÉCOMMANDE) ---
  const fileInputRef = useRef(null);
  const drawingInputRef = useRef(null);
  const previewImageRef = useRef(null);
  const textareaRef = useRef(null); // Ref pour le textarea

  // Auto-resize du textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [prompt]);

  // --- ÉTATS DESSIN ---
  const canvasRef1 = useRef(null);
  const canvasRef2 = useRef(null);
  const canvasRef3 = useRef(null);
  const canvasRefs = [canvasRef1, canvasRef2, canvasRef3];
  const contextRefs = useRef([null, null, null]);
  const interactionRef = useRef(null);

  const [activeLayer, setActiveLayer] = useState(1); // 1, 2, 3
  const [layerVisibility, setLayerVisibility] = useState({ 1: true, 2: true, 3: true });

  const [isDrawing, setIsDrawing] = useState(false);

  // Outils
  // Outils
  const [tool, setTool] = useState('pencil'); // 'pencil', 'line', 'eraser', 'smart_pencil'
  const [color, setColor] = useState('#000000');
  const [toolWidths, setToolWidths] = useState({
    pencil: 1,
    smart_pencil: 1,
    line: 1,
    eraser: 20,
  });
  // Helper: largeur de l'outil courant
  const lineWidth = toolWidths[tool] ?? 1;
  const setLineWidth = (val) => setToolWidths(prev => ({ ...prev, [tool]: val }));
  const [showThicknessControl, setShowThicknessControl] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const startPos = useRef({ x: 0, y: 0 });

  // --- TOOLBAR DRAG & ORIENTATION ---
  const [toolbarPos, setToolbarPos] = useState({ top: 10, left: 10 });
  const [isToolbarHorizontal, setIsToolbarHorizontal] = useState(false);
  const isDraggingToolbar = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleToolbarDragStart = (e) => {
    e.preventDefault();
    isDraggingToolbar.current = true;
    dragOffset.current = {
      x: e.clientX - toolbarPos.left,
      y: e.clientY - toolbarPos.top,
    };
    const onMove = (ev) => {
      if (!isDraggingToolbar.current) return;
      setToolbarPos({
        left: ev.clientX - dragOffset.current.x,
        top: ev.clientY - dragOffset.current.y,
      });
    };
    const onUp = () => {
      isDraggingToolbar.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  const smartPoints = useRef([]); // Points pour le tracé intelligent

  // --- UNDO / REDO HISTORY ---
  const [drawHistory, setDrawHistory] = useState([]);
  const [historyStep, setHistoryStep] = useState(-1);

  const saveDrawState = () => {
    if (!contextRefs.current[0] || !contextRefs.current[1] || !contextRefs.current[2]) return;
    
    const newHistory = drawHistory.slice(0, historyStep + 1);

    const layerStates = contextRefs.current.map((ctx, i) => {
       const canvas = canvasRefs[i].current;
       return ctx.getImageData(0, 0, canvas.width, canvas.height);
    });

    newHistory.push(layerStates);
    setDrawHistory(newHistory);
    setHistoryStep(newHistory.length - 1);
  };

  const undoDraw = () => {
    if (historyStep > 0) {
      const newStep = historyStep - 1;
      const layerStates = drawHistory[newStep];
      if (layerStates) {
        contextRefs.current.forEach((ctx, i) => {
          if (ctx) ctx.putImageData(layerStates[i], 0, 0);
        });
        setHistoryStep(newStep);
      }
    }
  };

  // Keyboard shortcut for Undo (Ctrl+Z)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Ctrl+Z or Cmd+Z
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        if (mode === 'draw') {
          e.preventDefault();
          undoDraw();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mode, drawHistory, historyStep]); // Re-bind when history changes to access latest state


  // Initialisation Canvas PAYSAGE (Ratio 16:9 pour éviter les déformations IA)
  const isCanvasInitialized = useRef(false);

  // Échelle dynamique du canvas (pour le curseur)
  const [canvasScale, setCanvasScale] = useState(1);
  useEffect(() => {
    const updateScale = () => {
      if (interactionRef.current && interactionRef.current.clientWidth > 0) {
        setCanvasScale(interactionRef.current.clientWidth / 1280);
      }
    };
    window.addEventListener('resize', updateScale);
    updateScale();
    const timeoutId = setTimeout(updateScale, 50); // post-layout
    return () => {
      window.removeEventListener('resize', updateScale);
      clearTimeout(timeoutId);
    };
  }, [mode]);

  useEffect(() => {
    if (mode === 'draw') {
      let allReady = true;
      for(let i=0; i<3; i++) {
         if (!canvasRefs[i].current) allReady = false;
      }
      if (!allReady) return;

      contextRefs.current = canvasRefs.map(ref => ref.current.getContext('2d'));

      if (!isCanvasInitialized.current) {
        canvasRefs.forEach(ref => {
          const canvas = ref.current;
          canvas.width = 1280;
          canvas.height = 720;
          canvas.style.width = '100%';
          canvas.style.height = '100%';
        });
        
        contextRefs.current.forEach(ctx => {
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
        });

        if (drawHistory.length === 0) {
          canvasRefs.forEach(ref => {
             const ctx = ref.current.getContext('2d');
             ctx.clearRect(0, 0, 1280, 720); 
          });

          setTimeout(() => {
            const layerStates = contextRefs.current.map(ctx => ctx.getImageData(0, 0, 1280, 720));
            setDrawHistory([layerStates]);
            setHistoryStep(0);
          }, 0);
        } else {
          const latestStates = drawHistory[historyStep];
          if (latestStates) {
            contextRefs.current.forEach((ctx, i) => { if (ctx) ctx.putImageData(latestStates[i], 0, 0); });
          }
        }
        isCanvasInitialized.current = true;
      } else {
        const latestStates = drawHistory[historyStep];
        if (latestStates) {
          contextRefs.current.forEach((ctx, i) => { if (ctx) ctx.putImageData(latestStates[i], 0, 0); });
        }
      }
    }
  }, [mode]);


  // Logique dessin
  const getCoords = (nativeEvent) => {
    const el = interactionRef.current;
    if (!el) return {x: 0, y: 0};
    const rect = el.getBoundingClientRect();
    return {
      x: (nativeEvent.clientX - rect.left) * (1280 / rect.width),
      y: (nativeEvent.clientY - rect.top) * (720 / rect.height)
    };
  };

  const startDrawing = ({ nativeEvent }) => {
    // Si une texture est sélectionnée, on place une pastille au lieu de dessiner
    if (selectedTexture) {
      const el = interactionRef.current;
      const rect = el.getBoundingClientRect();
      const xPct = ((nativeEvent.clientX - rect.left) / rect.width) * 100;
      const yPct = ((nativeEvent.clientY - rect.top) / rect.height) * 100;

      setTextureDots([...textureDots, {
        x: xPct,
        y: yPct,
        texture: selectedTexture
      }]);
      return;
    }

    const { x, y } = getCoords(nativeEvent);
    const ctx = contextRefs.current[activeLayer - 1];
    if (!ctx) return;

    let drawColor = color;
    if (activeLayer === 2) drawColor = '#cccccc';
    if (activeLayer === 3) drawColor = '#ff0000';

    if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = drawColor;
    }
    
    ctx.lineWidth = lineWidth;

    setIsDrawing(true);
    startPos.current = { x, y };

    setSnapshot(ctx.getImageData(0, 0, 1280, 720));

    ctx.beginPath();
    ctx.moveTo(x, y);

    if (tool === 'pencil' || tool === 'smart_pencil') {
      smartPoints.current = [{ x, y }];
    }
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing) return;
    const { x, y } = getCoords(nativeEvent);
    const ctx = contextRefs.current[activeLayer - 1];
    if (!ctx) return;

    if (tool === 'pencil' || tool === 'eraser' || tool === 'smart_pencil') {
      // Draw rough line for visual feedback
      ctx.lineTo(x, y);
      ctx.stroke();
      if (tool !== 'eraser') {
        smartPoints.current.push({ x, y });
      }
    } else if (tool === 'line') {
      ctx.putImageData(snapshot, 0, 0);
      ctx.beginPath();
      ctx.moveTo(startPos.current.x, startPos.current.y);
      
      let endX = x;
      let endY = y;
      
      if (nativeEvent.shiftKey) {
        const dx = Math.abs(x - startPos.current.x);
        const dy = Math.abs(y - startPos.current.y);
        if (dx > dy) {
          endY = startPos.current.y; // Lock to X axis (horizontal)
        } else {
          endX = startPos.current.x; // Lock to Y axis (vertical)
        }
      }

      ctx.lineTo(endX, endY);
      ctx.stroke();
    }
  };

  const finishDrawing = () => {
    if (!isDrawing) return;
    const ctx = contextRefs.current[activeLayer - 1];
    if (ctx) ctx.closePath();
    setIsDrawing(false);

    if (tool === 'pencil' && smartPoints.current.length > 2) {
      smoothPath(smartPoints.current);
      smartPoints.current = [];
    }

    if (tool === 'smart_pencil' && smartPoints.current.length > 1) {
      straightenPath(smartPoints.current);
      smartPoints.current = [];
    }

    if (ctx) ctx.globalCompositeOperation = 'source-over';

    saveDrawState();
  };

  const smoothPath = (points) => {
    const ctx = contextRefs.current[activeLayer - 1];
    ctx.putImageData(snapshot, 0, 0);

    let drawColor = color;
    if (activeLayer === 2) drawColor = '#cccccc';
    if (activeLayer === 3) drawColor = '#ff0000';

    ctx.strokeStyle = drawColor;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();

    if (points.length < 3) return;

    ctx.moveTo(points[0].x, points[0].y);

    for (let i = 1; i < points.length - 2; i++) {
      const xc = (points[i].x + points[i + 1].x) / 2;
      const yc = (points[i].y + points[i + 1].y) / 2;
      ctx.quadraticCurveTo(points[i].x, points[i].y, xc, yc);
    }

    ctx.quadraticCurveTo(
      points[points.length - 2].x,
      points[points.length - 2].y,
      points[points.length - 1].x,
      points[points.length - 1].y
    );

    ctx.stroke();
    ctx.closePath();
  };

  const straightenPath = (points) => {
    const ctx = contextRefs.current[activeLayer - 1];
    ctx.putImageData(snapshot, 0, 0);

    let drawColor = color;
    if (activeLayer === 2) drawColor = '#cccccc';
    if (activeLayer === 3) drawColor = '#ff0000';

    ctx.strokeStyle = drawColor;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    const start = points[0];
    const end = points[points.length - 1];
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    ctx.closePath();
  };

  const getMergedCanvas = () => {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = 1280;
    tempCanvas.height = 720;
    const tCtx = tempCanvas.getContext('2d');
    
    tCtx.fillStyle = 'white';
    tCtx.fillRect(0, 0, 1280, 720);

    // Render from bottom (3) to top (1)
    if (layerVisibility[3] && canvasRefs[2].current) tCtx.drawImage(canvasRefs[2].current, 0, 0);
    if (layerVisibility[2] && canvasRefs[1].current) tCtx.drawImage(canvasRefs[1].current, 0, 0);
    if (layerVisibility[1] && canvasRefs[0].current) tCtx.drawImage(canvasRefs[0].current, 0, 0);

    return tempCanvas;
  };

  const clearCanvas = (layerToClear = activeLayer) => {
    const ctx = contextRefs.current[layerToClear - 1];
    if (ctx) {
      ctx.clearRect(0, 0, 1280, 720);
      saveDrawState();
    }
  };

  const handleImportDrawingClick = () => {
    if (drawingInputRef.current) {
      drawingInputRef.current.value = ''; // Réinitialise pour permettre une réimportation
      drawingInputRef.current.click();
    }
  };

  const handleDrawingImport = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const ctx = contextRefs.current[activeLayer - 1];
        if (ctx) {
          ctx.drawImage(img, 0, 0, 1280, 720);
          saveDrawState();
        }
        URL.revokeObjectURL(url);
      };
      img.src = url;
    }
  };

  const saveDrawing = () => {
    const dataUrl = getMergedCanvas().toDataURL('image/png');
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `archivizai_dessin_${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Upload & Envoi
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (mode === 'model3d') {
        if (file.name.toLowerCase().endsWith('.obj')) {
          setModelFile(file);
          const url = URL.createObjectURL(file);
          setModelUrl(url);
          setModelDots([]);
          setError(null);
        } else {
          setError("Sélectionnez un fichier .obj");
        }
      } else {
        const objectUrl = URL.createObjectURL(file);
        setSelectedFile(file);
        setPreview(objectUrl);
        setHistory([{ url: objectUrl, type: 'original' }]); // Initialize History
        setViewIndex(0);
        setError(null);
        setTextureDots([]); // Reset dots on new file
      }
    }
  };

  // FONCTION POUR DÉCLENCHER L'OUVERTURE DE L'EXPLORATEUR
  const triggerFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePreviewClick = (e) => {
    if (!selectedTexture) return;

    const imgElement = e.target;
    // Verify we clicked on an image
    if (imgElement.tagName !== 'IMG') return;

    // --- ACCURATE COORDINATE MAPPING (Object-fit: contain) ---
    const rect = imgElement.getBoundingClientRect();
    const naturalWidth = imgElement.naturalWidth;
    const naturalHeight = imgElement.naturalHeight;
    const AR_img = naturalWidth / naturalHeight;
    const AR_el = rect.width / rect.height;

    let actualWidth, actualHeight, offsetX, offsetY;

    if (AR_img > AR_el) {
      // Letterbox (centered vertically)
      actualWidth = rect.width;
      actualHeight = rect.width / AR_img;
      offsetX = 0;
      offsetY = (rect.height - actualHeight) / 2;
    } else {
      // Pillarbox (centered horizontally)
      actualHeight = rect.height;
      actualWidth = rect.height * AR_img;
      offsetY = 0;
      offsetX = (rect.width - actualWidth) / 2;
    }

    const mouseX = e.clientX - rect.left - offsetX;
    const mouseY = e.clientY - rect.top - offsetY;

    // Percentage of the ACTUAL image pixels
    const x = (mouseX / actualWidth) * 100;
    const y = (mouseY / actualHeight) * 100;

    // Only add if within image boundaries
    if (x >= 0 && x <= 100 && y >= 0 && y <= 100) {
      setTextureDots([...textureDots, {
        x,
        y,
        texture: selectedTexture
      }]);
    }
  };

  const removeDot = (index, e) => {
    e.stopPropagation();
    setTextureDots(textureDots.filter((_, i) => i !== index));
  };

  const handleCapture3D = (e) => {
    // Prevent any form submission or bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!glContext || !modelUrl) return;

    try {
      // Capture the current canvas view
      const dataUrl = glContext.domElement.toDataURL('image/png');

      // Set as preview for the "Image" (Upload) mode
      setPreview(dataUrl);
      setHistory([{ url: dataUrl, type: 'original' }]); // Update history to show in carousel
      setViewIndex(0);
      setSelectedFile(null); // Clear any file selection as we are using a capture
      setTextureDots([]); // Reset dots for the new image

      // Switch mode
      setMode('upload');

    } catch (err) {
      console.error("Capture failed:", err);
      setError("Erreur lors de la capture de la vue 3D");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
    setLastPrompt('');

    if (onGenerationStart) onGenerationStart();

    const formData = new FormData();
    let finalPrompt = 'Génère une image ultraréaliste architectural basée sur cette image de bâtiment. Respecte impérativement les proportions des éléments entre eux. Chaque texture appliquée au bâtiment doit être à l\'échelle de la surface couverte.\n';

    // Ajouter l'image d'environnement si elle existe, sinon consigne par défaut
    if (environmentImage) {
      formData.append('environment_image', environmentImage);
    } else {
      finalPrompt += "\n- ENVIRONNEMENT : Génère un environnement réaliste avec de la végétation soignée.";
    }

    // Ajouter l'image de style si elle existe
    if (styleImage) {
      formData.append('style_image', styleImage);
      finalPrompt += '\n Inspire toi de l\'ambiance de cette image de bâtiment, Important tu ne dois pas pas prendre en compte les volume et proportion, la forme du batiment donné sur l\'image principal doit rester inchangé';
    }

    try {
      let imageSource;
      let dotsToProcess = [];
      let isPercentage = true;

      // PREPARATION DES DONNEES SELON LE MODE
      // NOTE: Le mode 'model3d' ne génère plus directement. Il passe par la capture vers 'upload'.
      // On garde le cas 'upload' et 'draw'.

      if (mode === 'upload') {
        // Nouvelle Logique : On prend l'image AFFICHEE ACTUELLEMENT via l'historique
        if (history.length > 0) {
          const currentHistoryItem = history[viewIndex];
          imageSource = currentHistoryItem ? currentHistoryItem.url : preview;
        } else {
          // Pas d'historique (Should be caught by check below but just in case)
          imageSource = preview;
        }

        if (!processTextures && !imageSource) throw new Error("Sélection manquante"); // Fallback check
        dotsToProcess = textureDots;
        isPercentage = true;

      } else if (mode === 'draw') {
        // Merge Drawing directly from canvas
        imageSource = getMergedCanvas().toDataURL('image/png');
        dotsToProcess = textureDots;
        isPercentage = true;
        
        // Mettre à jour l'aperçu et l'historique de l'espace image avec le dessin
        setPreview(imageSource);
        setHistory([{ url: imageSource, type: 'original' }]);
        setViewIndex(0);
        setMode('upload'); // Switch auto vers l'espace image
        
        finalPrompt += "\n IMPORTANT : Cette image est un croquis à main levée. Corrige impérativement les erreurs de perspective, redresse les lignes si nécessaire pour un rendu architectural professionnel tout en gardant l'intention du dessin.";
      } else {
        // Should not happen if UI is correct
        throw new Error("Mode non supporté pour la génération directe");
      }

      const blob = await (await fetch(imageSource)).blob(); // Get blob from dataURL
      // Note: processTextures handles the blob creation inside ? No, processTextures takes imageSource (url) and appends to formData
      // Let's check processTextures again. It takes imageSource string.

      // TRAITEMENT COMMUN (Texture + Image)
      const texturePromptPart = await processTextures(imageSource, dotsToProcess, formData, isPercentage);
      finalPrompt += texturePromptPart;

      //finalPrompt += ". Armonise l'éclairage et les perspectives pour un rendu ultra réaliste et hyper détaillé.";
      finalPrompt += "\n Le format d'image en sortis doit être en 16:9 et avoir une résolution 2k. IMPORTANT garde les proportion de l'image principal.";

      // FINALISATION
      if (prompt) finalPrompt += ` ${prompt}`; // Ajout du prompt utilisateur (dans le prompt global)

      setLastPrompt(finalPrompt);
      formData.append('prompt', finalPrompt);
      formData.append('user_prompt', prompt);

      await sendRequest(formData);

    } catch (err) {
      console.error(err);
      setError(err.message || 'Erreur');
      setLoading(false);
      if (onGenerationEnd) onGenerationEnd();
    }
  };


  const handleStopGeneration = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setLoading(false);
    if (onGenerationEnd) onGenerationEnd();
    console.log("Génération arrêtée par l'utilisateur.");
  };

  const sendRequest = async (formData) => {

    try {
      console.log("Sending request...");
      const response = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/transform/`, formData, {
        withCredentials: true,
      });

      if (response.status === 202 && response.data.task_id) {
        // Mode Async: On doit poller
        console.log("Task accepted, starting polling for ID:", response.data.task_id);
        pollTaskStatus(response.data.task_id);
      } else {
        // Mode Sync (Legacy ou immédiat)
        setResult(response.data);
        // Wait for image load
      }

    } catch (err) {
      console.error("Error in sendRequest:", err);
      if (err.response) {
        console.error("Error response:", err.response.data);
        console.error("Error status:", err.response.status);
      }
      setError('Erreur serveur: ' + (err.response?.data?.detail || err.message));
      setLoading(false);
      if (onGenerationEnd) onGenerationEnd();
    }
  };

  const pollTaskStatus = async (taskId) => {
    const pollUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/task-status/${taskId}/`;
    console.log(`[Polling] Starting polling for Task ID: ${taskId} at ${pollUrl}`);

    let attempts = 0;
    const maxAttempts = 60;

    // On utilise setInterval pour poller toutes les 2 secondes
    const interval = setInterval(async () => {
      pollingIntervalRef.current = interval;
      attempts++;

      console.log(`[Polling] Attempt ${attempts}/${maxAttempts}...`);

      try {
        const response = await axios.get(pollUrl);
        console.log(`[Polling] Response:`, response.data);

        if (response.data.status === 'SUCCESS') {
          clearInterval(interval);
          pollingIntervalRef.current = null;
          console.log("[Polling] Task SUCCESS! Result:", response.data.result);


          if (response.data.result && response.data.result.generated_image_url) {
            setResult(response.data.result);

            // Add to History
            const newImage = {
              url: response.data.result.generated_image_url,
              type: 'generated',
              id: Date.now() // Unique ID
            };

            setHistory(prev => {
              const newHistory = [...prev, newImage];
              // Update View Index handled by useEffect
              return newHistory;
            });

            // Wait for image load logic (handled in render via onLoad)
          } else {
            console.warn("[Polling] Success but no image URL found in result:", response.data);
            setLoading(false);
            if (onGenerationEnd) onGenerationEnd();
          }

        } else if (response.data.status === 'FAILURE') {
          clearInterval(interval);
          pollingIntervalRef.current = null;
          console.error("[Polling] Task FAILED:", response.data.error);

          setLoading(false);
          if (onGenerationEnd) onGenerationEnd();
          alert("Erreur lors de la génération: " + response.data.error);
        }
        // Si PENDING ou STARTED, on continue...

      } catch (error) {
        console.error(`[Polling] Error (Attempt ${attempts}):`, error);
        // On continue jusqu'au maxAttempts même en cas d'erreur réseau transitoire
      }

      if (attempts >= maxAttempts) {
        clearInterval(interval);
        pollingIntervalRef.current = null;
        setLoading(false);

        if (onGenerationEnd) onGenerationEnd();
        alert("Délai d'attente dépassé. La génération prend plus de temps que prévu.");
      }
    }, 2000); // 2 secondes
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreview(null);
    setPrompt('');
    setResult(null);
    setError(null);
    setTextureDots([]);
    setLastPrompt('');
    setHistory([]);
    setViewIndex(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (mode === 'model3d') {
      setModelFile(null);
      setModelUrl(null);
      setModelDots([]);
    }
    if (mode === 'draw') clearCanvas();
  };

  return (
    <div className="image-uploader">

      {/* MENU MODE REMOVED (Moved to Header) */}

      <div className="uploader-content">
        <form onSubmit={handleSubmit}>

          {/* IMPORTANT : L'INPUT EST MAINTENANT ICI, TOUJOURS PRESENT MAIS CACHÉ */}
          <input
            type="file"
            id="image-upload"
            accept={mode === 'model3d' ? ".obj" : "image/*"}
            onChange={handleFileChange}
            className="file-input"
            ref={fileInputRef}
          />
          {/* INPUT POUR L'IMPORT DE DESSIN */}
          <input
            type="file"
            accept="image/*"
            onChange={handleDrawingImport}
            className="file-input"
            ref={drawingInputRef}
            style={{ display: 'none' }}
          />

          {/* ZONE IMPORT / RESULTAT (CARROUSEL) */}
          <div style={{ display: mode === 'upload' ? 'flex' : 'none', flex: 1, flexDirection: 'column', minHeight: 0 }}>
            <div className={`input-zone ${history.length === 0 ? 'centered' : ''}`}>

              {/* CAS 1 : RÉSULTAT DISPONIBLE (Affichage Carrousel) */}
              {/* MODE CARROUSEL / HISTORY GRID */}
              {history.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>

                  {/* MAIN CAROUSEL VIEW */}
                  <div className="carousel-container" style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>

                    {/* Actions (Top Right) */}
                    <div className="result-actions-top-right">
                      <button
                        className="action-icon-btn"
                        type="button"
                        onClick={() => {
                          const currentImg = history[viewIndex];
                          if (currentImg) {
                            const link = document.createElement('a');
                            link.href = currentImg.url;
                            link.download = `archivizai_${currentImg.type}_${Date.now()}.png`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);

                          }
                        }}
                        title="Télécharger"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                      </button>
                      <button
                        className="action-icon-btn"
                        type="button"
                        onClick={handleReset}
                        title="Nouvelle Étude"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="16"></line><line x1="8" y1="12" x2="16" y2="12"></line></svg>
                      </button>
                    </div>

                    {/* Active Image */}
                    <div className="carousel-image-wrapper" style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <div className="image-tight-container" style={{ position: 'relative', width: 'fit-content', height: 'fit-content', maxWidth: '100%', maxHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {history.map((item, index) => (
                          <img
                            key={item.id || index}
                            src={item.url}
                            alt={`Vue ${index}`}
                            className={`carousel-image ${index === 0 ? 'original-base' : 'generated-overlay'}`}
                            onClick={(e) => { if (index === viewIndex) handlePreviewClick(e); }}
                            style={{
                              position: index === 0 ? 'relative' : 'absolute',
                              top: index === 0 ? 'auto' : 0,
                              left: index === 0 ? 'auto' : 0,
                              width: index === 0 ? 'auto' : '100%',
                              height: index === 0 ? 'auto' : '100%',
                              maxWidth: '100%',
                              maxHeight: '75vh', // Constrain height to leave room for UI
                              zIndex: index === viewIndex ? 10 : 5,
                              opacity: index === viewIndex ? 1 : 0,
                              pointerEvents: index === viewIndex ? 'auto' : 'none',
                              transition: 'opacity 1.2s ease-in-out',
                              cursor: selectedTexture ? `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"><circle cx="10" cy="10" r="8" fill="${encodeURIComponent(selectedTexture.color)}" stroke="white" stroke-width="2"/></svg>') 10 10, auto` : 'default',
                              display: 'block'
                            }}
                            onLoad={() => {
                              // Stop loading only when the *generated* result image loads.
                              // (L'image "original" - ex: dessin capturé - se charge quasi instantanément
                              // et ne doit jamais couper l'animation/le bouton STOPPER en cours de génération.)
                              if (loading && item.type === 'generated' && index === history.length - 1) {
                                setLoading(false);
                                if (onGenerationEnd) onGenerationEnd();
                              }
                            }}
                          />
                        ))}


                        {/* Render Dots */}
                        {showTextureDots && textureDots.map((dot, index) => (
                          <div
                            key={index}
                            className="texture-dot"
                            style={{
                              left: `${dot.x}%`,
                              top: `${dot.y}%`,
                              backgroundColor: dot.texture.color,
                            }}
                            onClick={(e) => removeDot(index, e)}
                            title={dot.texture.name}
                          ></div>
                        ))}

                        {/* Navigation Arrows (Inside Image Container to be close) */}
                        {viewIndex > 0 && (
                          <button
                            type="button"
                            className="nav-arrow left"
                            onClick={(e) => { e.stopPropagation(); setViewIndex(prev => Math.max(0, prev - 1)); }}
                            title="Précédent"
                          >
                            &#10094;
                          </button>
                        )}
                        {viewIndex < history.length - 1 && (
                          <button
                            type="button"
                            className="nav-arrow right"
                            onClick={(e) => { e.stopPropagation(); setViewIndex(prev => Math.min(history.length - 1, prev + 1)); }}
                            title="Suivant"
                          >
                            &#10095;
                          </button>
                        )}
                      </div>
                    </div>


                  </div>



                </div>
              ) : (
                /* CAS 2 : PAS D'HISTORIQUE (Aucun fichier sélectionné) */
                <>
                  <label htmlFor="image-upload" className="huge-label">
                    {loading ? "CALCUL EN COURS..." : "IMPORTER UNE IMAGE"}
                  </label>
                </>
              )}
            </div>
          </div>

          {/* ZONE MODEL 3D */}
          <div style={{ display: mode === 'model3d' ? 'flex' : 'none', flex: 1, flexDirection: 'column', minHeight: 0 }}>
            <div className="input-zone">
              {!modelUrl ? (
                <label htmlFor="image-upload" className="huge-label">IMPORTER UN .OBJ</label>
              ) : (
                <div className="preview-container" style={{
                  width: '100%',
                  flex: 1,
                  margin: '0 auto',
                  position: 'relative',
                  minHeight: 0
                }}>
                  <Canvas
                    gl={{ preserveDrawingBuffer: true }}
                    onCreated={({ gl }) => setGlContext(gl)}
                    camera={{ position: [3, 3, 3], fov: 50 }}
                  >
                    <ambientLight intensity={0.8} />
                    <directionalLight position={[10, 10, 5]} intensity={1} />
                    <Environment preset="studio" />
                    <Center>
                      <Model
                        url={modelUrl}
                        selectedTexture={null} // Pas de texture en mode 3D pur
                        modelDots={[]} // Pas de dots affichés
                        setModelDots={() => { }} // Pas d'ajout de dots
                      />
                    </Center>
                    <OrbitControls
                      makeDefault
                      minPolarAngle={0}
                      maxPolarAngle={Math.PI}
                      enableDamping={true}
                      dampingFactor={0.05}
                    />
                    <Projector modelDots={modelDots} />
                    <RecenterEvents />
                  </Canvas>

                  <button type="button" onClick={triggerFileSelect} className="clear-text-btn" style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
                    CHANGER DE MODÈLE
                  </button>

                  <div className="controls-infographic">
                    <div className="control-item">
                      <span className="mouse-icon left-click"></span>
                      <span>Rotation</span>
                    </div>
                    <div className="control-item">
                      <span className="mouse-icon right-click"></span>
                      <span>Déplacement</span>
                    </div>
                    <div className="control-item">
                      <span className="mouse-icon wheel"></span>
                      <span>Zoom</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ZONE DESSIN */}
          <div style={{ display: mode === 'draw' ? 'flex' : 'none', height: '100%', width: '100%', flex: 1, flexDirection: 'column', minHeight: 0 }}>
            <div className="canvas-container" style={{
              position: 'relative',
              width: '100%', // Restore full width
              height: '100%', // Restore full height
              margin: '0', // No margin
              overflow: 'hidden'
            }}>

              {/* Actions de Dessin (Top Right) */}
              <div className="result-actions-top-right">
                <button
                  className="action-icon-btn"
                  type="button"
                  onClick={saveDrawing}
                  title="Sauvegarder le dessin"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                </button>
                <button
                  className="action-icon-btn"
                  type="button"
                  onClick={handleImportDrawingClick}
                  title="Importer un dessin"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                </button>
              </div>

              {/* NEW TOOLBAR (Top-Left inside canvas area) */}
              <div
                className={`architect-toolbar-modern ${isToolbarHorizontal ? 'horizontal' : ''}`}
                style={{ top: toolbarPos.top, left: toolbarPos.left }}
              >
                {/* DRAG HANDLE */}
                <div
                  className="toolbar-drag-handle"
                  onMouseDown={handleToolbarDragStart}
                  title="Déplacer la barre d'outils"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                    <circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/>
                    <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
                    <circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/>
                  </svg>
                </div>
                <div className="tools-group-icons">

                  {/* PENCIL */}
                  <div className="tool-wrapper">
                    <button
                      type="button"
                      className={`tool-icon-btn ${tool === 'pencil' ? 'active' : ''}`}
                      onClick={() => {
                        setTool('pencil');
                        setShowThicknessControl(tool === 'pencil' ? !showThicknessControl : true);
                      }}
                      title="Crayon (Correction automatique)"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>
                    </button>
                    {/* POPUP ALIGNED HERE */}
                    {showThicknessControl && tool === 'pencil' && (
                      <div className="thickness-popup">
                        <input
                          type="range"
                          min="1"
                          max="50"
                          value={toolWidths.pencil}
                          onChange={(e) => setToolWidths(prev => ({ ...prev, pencil: parseInt(e.target.value) }))}
                        />
                        <span className="thickness-val">{toolWidths.pencil}px</span>
                      </div>
                    )}
                  </div>

                  {/* SMART PENCIL */}
                  <div className="tool-wrapper">
                    <button
                      type="button"
                      className={`tool-icon-btn ${tool === 'smart_pencil' ? 'active' : ''}`}
                      onClick={() => {
                        setTool('smart_pencil');
                        setShowThicknessControl(tool === 'smart_pencil' ? !showThicknessControl : true);
                      }}
                      title="Plume (Ligne Droite)"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"></path><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path><path d="M2 2l7.586 7.586"></path><circle cx="11" cy="11" r="2"></circle></svg>
                    </button>
                    {showThicknessControl && tool === 'smart_pencil' && (
                      <div className="thickness-popup">
                        <input
                          type="range"
                          min="1"
                          max="50"
                          value={toolWidths.smart_pencil}
                          onChange={(e) => setToolWidths(prev => ({ ...prev, smart_pencil: parseInt(e.target.value) }))}
                        />
                        <span className="thickness-val">{toolWidths.smart_pencil}px</span>
                      </div>
                    )}
                  </div>

                  {/* LINE */}
                  <div className="tool-wrapper">
                    <button
                      type="button"
                      className={`tool-icon-btn ${tool === 'line' ? 'active' : ''}`}
                      onClick={() => {
                        setTool('line');
                        setShowThicknessControl(tool === 'line' ? !showThicknessControl : true);
                      }}
                      title="Ligne"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                    </button>
                    {showThicknessControl && tool === 'line' && (
                      <div className="thickness-popup">
                        <input
                          type="range"
                          min="1"
                          max="50"
                          value={toolWidths.line}
                          onChange={(e) => setToolWidths(prev => ({ ...prev, line: parseInt(e.target.value) }))}
                        />
                        <span className="thickness-val">{toolWidths.line}px</span>
                      </div>
                    )}
                  </div>

                  {/* ERASER */}
                  <div className="tool-wrapper">
                    <button
                      type="button"
                      className={`tool-icon-btn ${tool === 'eraser' ? 'active' : ''}`}
                      onClick={() => {
                        setTool('eraser');
                        setShowThicknessControl(tool === 'eraser' ? !showThicknessControl : true);
                      }}
                      title="Gomme"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 20H7L3 16C2 15 2 13 3 12L13 2L22 11L20 20Z"></path><line x1="9" y1="9" x2="20" y2="20"></line></svg>
                    </button>
                    {showThicknessControl && tool === 'eraser' && (
                      <div className="thickness-popup">
                        <input
                          type="range"
                          min="1"
                          max="100"
                          value={toolWidths.eraser}
                          onChange={(e) => setToolWidths(prev => ({ ...prev, eraser: parseInt(e.target.value) }))}
                        />
                        <span className="thickness-val">{toolWidths.eraser}px</span>
                      </div>
                    )}
                  </div>

                  {/* GLOBAL THICKNESS SLIDER REMOVED (Replaced by per-tool popups) */}

                  {/* UNDO (Placeholder / Clear) */}
                  <div className="tool-wrapper">
                    <button
                      type="button"
                      className="tool-icon-btn"
                      onClick={() => clearCanvas(activeLayer)}
                      title="Effacer le calque actif"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path></svg>
                    </button>
                  </div>
                </div>

                {/* CALQUES (Layers) */}
                <div className="layers-group-vertical" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center', borderTop: '1px solid #eee', paddingTop: '10px' }}>
                  {[1, 2, 3].map(layerNum => (
                    <div key={layerNum} className={`layer-item ${activeLayer === layerNum ? 'active' : ''}`} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      <button
                        type="button"
                        className={`tool-icon-btn ${activeLayer === layerNum ? 'active' : ''}`}
                        onClick={() => setActiveLayer(layerNum)}
                        title={`Calque ${layerNum}`}
                        style={{ color: layerNum === 1 ? (activeLayer === 1 ? '#fff' : 'inherit') : (layerNum === 2 ? '#999' : '#e11d48'), fontSize: '0.85rem', fontWeight: 'bold' }}
                      >
                        C{layerNum}
                      </button>
                      <div className="layer-actions" style={{ display: 'flex', gap: '4px' }}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setLayerVisibility(prev => ({...prev, [layerNum]: !prev[layerNum]})); }}
                          title={layerVisibility[layerNum] ? "Masquer" : "Afficher"}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '0px' }}
                        >
                          {layerVisibility[layerNum] ? '👁️' : '👁️‍🗨️'}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); clearCanvas(layerNum); }}
                          title="Effacer"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '0px' }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* COLORS (Only show if Layer 1 is active, else hide) */}
                <div className="colors-group-vertical" style={{ display: activeLayer === 1 ? 'flex' : 'none' }}>
                  <button type="button" className={`color-dot-mini black ${color === '#000000' ? 'active' : ''}`} onClick={() => setColor('#000000')} title="Noir"></button>
                  <button type="button" className={`color-dot-mini green ${color === '#16a34a' ? 'active' : ''}`} onClick={() => setColor('#16a34a')} title="Vert"></button>
                  <button type="button" className={`color-dot-mini blue ${color === '#3b82f6' ? 'active' : ''}`} onClick={() => setColor('#3b82f6')} title="Bleu"></button>
                </div>

                {/* TOGGLE ORIENTATION */}
                <button
                  type="button"
                  className="tool-icon-btn toggle-orientation-btn"
                  onClick={() => setIsToolbarHorizontal(!isToolbarHorizontal)}
                  title="Changer l'orientation"
                  style={{
                    marginTop: isToolbarHorizontal ? 0 : 'auto',
                    marginLeft: isToolbarHorizontal ? 'auto' : 0,
                    border: 'none',
                    background: 'transparent',
                    boxShadow: 'none',
                  }}
                >
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" 
                       style={{ transform: isToolbarHorizontal ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}>
                    <rect x="3" y="3" width="18" height="6" rx="2" ry="2"></rect>
                    <rect x="3" y="15" width="6" height="6" rx="2" ry="2"></rect>
                  </svg>
                </button>
              </div>

              {/* Canvas Wrapper - Maximized with Aspect Ratio 16:9 */}
              <div style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',
                backgroundColor: '#f5f5f5' // fond gris léger pour repérer la zone 16:9 si l'écran est par ex 21:9
              }}>
                <div style={{
                  position: 'relative',
                  height: '100%',
                  maxHeight: '100%',
                  maxWidth: '100%',
                  aspectRatio: '16 / 9',
                  boxShadow: '0 20px 50px -10px rgba(0,0,0,0.15)',
                  backgroundColor: 'white' // base blanche
                }}>
                  {/* LAYER 3 (Bottom) */}
                  <canvas
                    ref={canvasRefs[2]}
                    className="architect-canvas"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: layerVisibility[3] ? 1 : 0, zIndex: 1, background: 'transparent' }}
                  />

                  {/* LAYER 2 (Middle) */}
                  <canvas
                    ref={canvasRefs[1]}
                    className="architect-canvas"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: layerVisibility[2] ? 1 : 0, zIndex: 2, background: 'transparent' }}
                  />

                  {/* LAYER 1 (Top) */}
                  <canvas
                    ref={canvasRefs[0]}
                    className="architect-canvas"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: layerVisibility[1] ? 1 : 0, zIndex: 3, background: 'transparent' }}
                  />

                  {/* INTERACTION OVERLAY */}
                  <div
                    ref={interactionRef}
                    onMouseDown={startDrawing}
                    onMouseUp={finishDrawing}
                    onMouseMove={draw}
                    onMouseLeave={finishDrawing}
                    style={{ 
                      position: 'absolute', 
                      top: 0, left: 0, 
                      width: '100%', 
                      height: '100%', 
                      zIndex: 10,
                      cursor: tool === 'eraser' 
                        ? (() => {
                            const visualSize = Math.max(toolWidths.eraser * canvasScale, 4);
                            const half = Math.round(visualSize / 2);
                            const size = half * 2;
                            // Pour un outil gomme sur calque transparent, retirer le fond blanc du curseur
                            const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}"><circle cx="${half}" cy="${half}" r="${Math.max(half - 1, 1)}" fill="none" stroke="#000000" stroke-width="1.5"/><circle cx="${half}" cy="${half}" r="${Math.max(half - 2.5, 0.5)}" fill="none" stroke="#ffffff" stroke-width="1.5"/></svg>`;
                            return `url('data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}') ${half} ${half}, crosshair`;
                          })()
                        : 'crosshair'
                    }}
                  />
                {/* DOTS OVERLAY FOR DRAW MODE */}
                {textureDots.map((dot, index) => (
                  <div
                    key={index}
                    className="texture-dot"
                    style={{
                      left: `${dot.x}%`,
                      top: `${dot.y}%`,
                      backgroundColor: dot.texture.color,
                    }}
                    onClick={(e) => removeDot(index, e)}
                    title={dot.texture.name}
                  ></div>
                ))}
                </div>
              </div>
            </div>
          </div>



          <div className="prompt-container">
            {mode !== 'model3d' && (
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="INSTRUCTIONS..."
                rows="1"
                style={{ overflow: 'hidden' }} // Empêche la scrollbar
              />
            )}
          </div>

          {error && <div className="error-text">{error}</div>}

          <div className="actions">
            {mode === 'model3d' ? (
              <button
                type="button"
                onClick={handleCapture3D}
                disabled={!modelUrl}
                className="submit-text-btn"
                style={{ color: '#3a3a3a' }} // Style distinctif (bleu par exemple)
              >
                CAPTURER LA VUE
              </button>
            ) : (
              <button
                type={loading ? "button" : "submit"}
                onClick={loading ? handleStopGeneration : undefined}
                disabled={(mode === 'upload' && !preview && !selectedFile && history.length === 0) || (userData && userData.credits <= 0 && !loading)} // Enabled during loading to allow STOP
                className={`submit-text-btn ${loading ? 'stop-mode' : ''}`}
              >
                {loading ? 'STOPPER LA GÉNÉRATION' : (userData && userData.credits <= 0 ? 'CRÉDITS ÉPUISÉS' : 'GÉNÉRER LE PROJET')}
              </button>


            )}
          </div>



          {
            result && result.image && (
              <div className="result-block">
                <div className="result-img-wrapper">
                  {result.image.generated_image_url ? (
                    <div className="result-images-container">
                      {/* Image uploadée en petit en haut à gauche */}
                      {result.image.original_image_url && (
                        <img
                          src={result.image.original_image_url}
                          alt="Original"
                          className="result-original-small"
                          onError={(e) => {
                            console.error('Image load error:', e.target.src);
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      {/* Image générée en grand */}
                      <img
                        src={result.image.generated_image_url}
                        alt="Généré"
                        className="result-generated-large"
                        onError={(e) => {
                          console.error('Image load error:', e.target.src);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  ) : result.image.original_image_url ? (
                    <img
                      src={result.image.original_image_url}
                      alt="Original"
                      className="final-image"
                      onError={(e) => {
                        console.error('Image load error:', e.target.src);
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="no-image-message">Aucune image disponible</div>
                  )}
                </div>

                {/* Affichage du prompt utilisé */}
                {lastPrompt && (
                  <div className="generated-prompt-display" style={{ marginTop: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '5px', fontSize: '0.9em', color: '#333', textAlign: 'left' }}>
                    <strong>Prompt utilisé :</strong>
                    <p style={{ whiteSpace: 'pre-wrap', marginTop: '5px' }}>{lastPrompt}</p>
                  </div>
                )}

                {/* Button Removed - Now in Header/Overlay */}
              </div>
            )
          }
        </form >
      </div >
    </div >
  );
};

// Helper components for 3D
const Model = ({ url, selectedTexture, modelDots, setModelDots }) => {
  const obj = useLoader(OBJLoader, url);
  const { controls } = useThree();

  useEffect(() => {
    if (obj) {
      // 1. Calculer le centre (BoundingBox)
      const box = new THREE.Box3().setFromObject(obj);
      const center = new THREE.Vector3();
      box.getCenter(center);

      // 2. Recentrer la géométrie elle-même
      // On déplace l'objet pour que son centre mathématique soit à (0,0,0)
      obj.position.sub(center);

      // 3. Correction d'orientation (Archicad Z-up -> Three.js Y-up)
      obj.rotation.x = -Math.PI / 2;

      // 4. Style "Maquette" : Ajout des arrêtes visibles
      obj.traverse((child) => {
        if (child.isMesh) {
          // On évite d'ajouter plusieurs fois si le composant est re-monté
          if (!child.userData.hasEdges) {
            // Création de la géométrie des arrêtes (threshold angle 30°)
            const edges = new THREE.EdgesGeometry(child.geometry, 30);
            const line = new THREE.LineSegments(
              edges,
              new THREE.LineBasicMaterial({ color: 0x333333 }) // Gris foncé plutôt que noir pur pour un style plus doux
            );
            child.add(line);
            child.userData.hasEdges = true;
          }
        }
      });

      // 5. Update des contrôles
      if (controls) {
        controls.reset();
        controls.update();
      }
    }
  }, [obj, controls]);

  const handleClick = (e) => {
    if (!selectedTexture) return;
    e.stopPropagation();
    // e.point is the 3D point of intersection
    setModelDots([...modelDots, {
      position: [e.point.x, e.point.y, e.point.z],
      texture: selectedTexture
    }]);
  };

  return (
    <primitive
      object={obj}
      onClick={handleClick}
      onPointerOver={() => document.body.style.cursor = selectedTexture ? 'crosshair' : 'default'}
      onPointerOut={() => document.body.style.cursor = 'default'}
    >
      {modelDots.map((dot, i) => (
        <mesh key={i} position={dot.position}>
          <sphereGeometry args={[0.1, 16, 16]} />
          <meshBasicMaterial color={dot.texture.color} />
        </mesh>
      ))}
    </primitive>
  );
};

const Projector = ({ modelDots }) => {
  const { camera, size } = useThree();

  useEffect(() => {
    // Project dots to 2D normalized coordinates (0-1)
    const projected = modelDots.map(dot => {
      const vector = new THREE.Vector3(...dot.position);
      vector.project(camera);
      // vector.x and .y are in range [-1, 1]
      // Convert to [0, 1]
      const x = (vector.x + 1) / 2;
      const y = -(vector.y - 1) / 2; // Invert Y because canvas Y is top-down
      return { x, y, texture: dot.texture };
    });

    // Store in a global or window variable to be accessed by submit
    // (A bit dirty but avoids complex state lifting for this quick implementation)
    window.currentProjectedDots = projected;
  }); // Run on every frame/render

  return null;
};



const RecenterEvents = () => {
  const { controls, gl } = useThree();
  const lastClick = useRef(0);

  useEffect(() => {
    const handlePointerDown = (e) => {
      // Check for Middle Mouse Button (button 1)
      if (e.button === 1) {
        const now = Date.now();
        if (now - lastClick.current < 300) {
          // Double click detected
          if (controls) controls.reset();
        }
        lastClick.current = now;
      }
    };

    const canvas = gl.domElement;
    canvas.addEventListener('pointerdown', handlePointerDown);
    return () => canvas.removeEventListener('pointerdown', handlePointerDown);
  }, [controls, gl]);

  return null;
};

export default ImageUploader;